import React, { useContext, useState } from 'react';
import { Types } from '../../../enums/TypeEnums';
import {
  IMeetingsMeetingType,
  useCreateMeetingTypeMutation,
  useUpdateMeetingTypeMutation,
  useGetMeetingTypesQuery,
  GetMeetingTypesDocument,
  IGetMeetingTypesQuery,
  MeetingsMeetingTypeDocument,
  IMeetingsMeetingTypeQuery
} from '../../../generated/graphql';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import DialogBox from '../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import InputComponent from '../../Common/Fields/InputComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import EditButton from '../../Common/Buttons/EditButton';
import { canUserEdit } from '../../../utils/rightUtils';
import { Colors } from '../../../utils/theme';

type IProps = {
  handleClose: (saved: boolean, isNew?: boolean, meetingTypeInput?: IMeetingsMeetingType) => void;
  meetingTypeInput: IMeetingsMeetingType | null;
};

export type IErrors = {
  name: string | null;
};

const defaultErrorsObj: IErrors = {
  name: null
};

const errorTexts = {
  name: 'Name is required!',
  nameExists: 'Meeting type name already exists'
};

const MeetingTypeForm: React.FunctionComponent<IProps> = ({
  handleClose,
  meetingTypeInput
}: IProps) => {
  const [editable, setEditable] = useState(meetingTypeInput === null);
  const [name, setName] = useState(meetingTypeInput?.name || '');
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createMeetingType, createResult] = useCreateMeetingTypeMutation();
  const { dispatch, state } = useContext(AppContext);
  const [updateMeetingType, updateMeetingTypeResult] = useUpdateMeetingTypeMutation();
  const { data: meetingTypesData } = useGetMeetingTypesQuery();
  const canEdit = canUserEdit(state.user, Types.Index);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ name: null });
    } else {
      setFormErrors({ name: errorTexts.name });
    }
    setName(value);
  };

  const handleCancel = () => {
    if (editable && meetingTypeInput !== null) {
      setName(meetingTypeInput?.name || '');
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose(false);
    }
  };

  const checkNameExists = (nameToCheck: string, excludeId?: string): boolean => {
    if (!meetingTypesData?.result) {
      return false;
    }
    return meetingTypesData.result.some(
      (mt) =>
        mt && mt.name.toLowerCase() === nameToCheck.toLowerCase().trim() && mt.id !== excludeId
    );
  };

  const handleSubmit = () => {
    const trimmedName = name ? name.trim() : '';
    const errors = {
      name: null
    } as IErrors;
    let hasErrors = false;
    if (trimmedName.length === 0) {
      hasErrors = true;
      errors.name = errorTexts.name;
    } else if (checkNameExists(trimmedName, meetingTypeInput?.id)) {
      hasErrors = true;
      errors.name = errorTexts.nameExists;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(meetingTypeInput === null ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    if (type === 'create') {
      createMeetingType({
        variables: {
          name: (name as string).trim()
        },
        update(cache, { data }) {
          const newMeetingType = data?.result as IMeetingsMeetingType;

          // Update getMeetingTypes cache (used by MeetingTypeList)
          try {
            const meetingTypesQuery = cache.readQuery({
              query: GetMeetingTypesDocument
            }) as IGetMeetingTypesQuery;

            if (meetingTypesQuery && meetingTypesQuery.result) {
              cache.writeQuery({
                query: GetMeetingTypesDocument,
                data: {
                  result: [newMeetingType, ...meetingTypesQuery.result]
                }
              });
            }
          } catch (error) {}

          // Update meetingsMeetingType cache (used by NewMeeting component)
          try {
            const meetingsMeetingTypeQuery = cache.readQuery({
              query: MeetingsMeetingTypeDocument
            }) as IMeetingsMeetingTypeQuery;

            if (meetingsMeetingTypeQuery && meetingsMeetingTypeQuery.result) {
              cache.writeQuery({
                query: MeetingsMeetingTypeDocument,
                data: {
                  result: [newMeetingType, ...meetingsMeetingTypeQuery.result]
                }
              });
            }
          } catch (error) {}

          handleClose(true, true, newMeetingType);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Meeting type ${name} created`
            }
          });
        }
      }).catch((error) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: error.message,
            autoHide: 6000
          }
        });
      });
    } else {
      updateMeetingType({
        variables: {
          id: meetingTypeInput?.id as string,
          name: (name as string).trim()
        },
        update(cache, { data }) {
          const updatedMeetingType = data?.result as IMeetingsMeetingType;

          // Update getMeetingTypes cache (used by MeetingTypeList)
          try {
            const meetingTypesQuery = cache.readQuery({
              query: GetMeetingTypesDocument
            }) as IGetMeetingTypesQuery;

            if (meetingTypesQuery && meetingTypesQuery.result) {
              const updatedData = meetingTypesQuery.result.map((m) =>
                m?.id === updatedMeetingType.id ? updatedMeetingType : m
              );
              cache.writeQuery({
                query: GetMeetingTypesDocument,
                data: {
                  result: updatedData
                }
              });
            }
          } catch (error) {}

          // Update meetingsMeetingType cache (used by NewMeeting component)
          try {
            const meetingsMeetingTypeQuery = cache.readQuery({
              query: MeetingsMeetingTypeDocument
            }) as IMeetingsMeetingTypeQuery;

            if (meetingsMeetingTypeQuery && meetingsMeetingTypeQuery.result) {
              const updatedData = meetingsMeetingTypeQuery.result.map((m) =>
                m?.id === updatedMeetingType.id ? updatedMeetingType : m
              );
              cache.writeQuery({
                query: MeetingsMeetingTypeDocument,
                data: {
                  result: updatedData
                }
              });
            }
          } catch (error) {}

          handleClose(true, false, updatedMeetingType);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Meeting type updated'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="new-meeting-type"
      width={500}
      title={meetingTypeInput ? `${meetingTypeInput.name} Details` : 'Create Meeting Type'}
      onClose={() => handleClose(false)}
    >
      {meetingTypeInput && (
        <Box sx={{ textAlign: 'right', width: '100%', marginTop: '10px', marginRight: '15px' }}>
          <EditButton
            id="meeting-type-form-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable || !canEdit}
            editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
          padding: '32px 30px',
          height: meetingTypeInput ? '200px' : '240px'
        }}
      >
        <InputComponent
          id="meeting-type-form-name"
          label="Name"
          value={name || ''}
          onChange={handleNameChange}
          errorMsg={formErrors.name}
          disabled={!editable || createResult.loading || updateMeetingTypeResult.loading}
          additionalStyle={{ gridColumn: 'span 1' }}
        />
        <Box className="centralize" sx={{ gridColumn: 'span 1' }}>
          <CancelButton
            id="cancel-meeting-type-button"
            disabled={createResult.loading || updateMeetingTypeResult.loading}
            onClick={handleCancel}
            text={meetingTypeInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-meeting-type-button"
            disabled={!editable || createResult.loading || updateMeetingTypeResult.loading}
            onClick={handleSubmit}
            text={meetingTypeInput === null ? 'Create' : 'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default MeetingTypeForm;
